import { useEffect, useRef, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createApiClient, type paths } from '@snaphire/api-client';
import { apiError, cookieValue } from './api/session';
import { statusLabel } from './i18n';
import { ApplicationQuestion, applicationAnswers, type Question } from './application-fields';
import { Select, Button, Empty, ErrorState, Field, Loading, Status } from './ui';
import { EMAIL_ENABLED } from './features';

const publicApi = createApiClient({ credentials: 'include' });
const headers = () => ({ 'Idempotency-Key': crypto.randomUUID() });
type PublicResult = { data?: unknown; error?: unknown; response: Response };
async function publicCommand(
  path: keyof paths,
  body: unknown,
  params: Record<string, string> = {},
  csrf?: string,
): Promise<Record<string, unknown>> {
  const result = await (
    publicApi.POST as unknown as (path: keyof paths, init: unknown) => Promise<PublicResult>
  )(path, {
    body,
    params: { path: params, header: headers() },
    headers: csrf ? { 'X-CSRF-Token': csrf } : {},
  });
  if (result.error || !result.response.ok) throw apiError(result.response, result.error);
  return (result.data || {}) as Record<string, unknown>;
}
export function isPublicPage(path: string) {
  return path.startsWith('/careers') || ['/offer', '/withdraw-consent'].includes(path);
}
export function PublicPortal() {
  const path = useRouterState({ select: (state) => state.location.pathname });
  return (
    <div className="public-app">
      <header className="public-header">
        <Link to="/careers" className="brand">
          <img src="/assets/snaphire-icon.png" alt="" />
          <span>SnapHire</span>
        </Link>
        <Link to="/">Вход для сотрудников</Link>
      </header>
      <main className="public-main">
        {path === '/offer' ? (
          <PublicOffer />
        ) : path === '/withdraw-consent' ? (
          <ConsentWithdrawal />
        ) : path === '/careers' ? (
          <PublicJobs />
        ) : (
          <PublicJob id={path.split('/')[2] || ''} applying={path.endsWith('/apply')} />
        )}
      </main>
      <footer className="public-footer">
        <Link to="/withdraw-consent">Управление согласием на обработку данных</Link>
      </footer>
    </div>
  );
}
function PublicJobs() {
  const list = useInfiniteQuery({
    queryKey: ['public-jobs'],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const result = await publicApi.GET('/public/v1/job-postings', {
        params: { query: { cursor: pageParam, limit: 100 } },
        signal,
      });
      if (!result.data) throw apiError(result.response, result.error);
      return result.data;
    },
    getNextPageParam: (page) => (page.page.hasMore ? page.page.nextCursor || undefined : undefined),
  });
  return (
    <>
      <h1>Открытые вакансии</h1>
      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <ErrorState error={list.error} />
      ) : !list.data?.pages.some((page) => page.items.length) ? (
        <Empty title="Открытых вакансий пока нет" />
      ) : (
        <div className="public-job-list">
          {list.data.pages
            .flatMap((page) => page.items)
            .map((job) => (
              <Link
                className="panel public-job-card"
                to="/careers/$publicId"
                params={{ publicId: job.publicId }}
                key={job.publicId}
              >
                <h2>{job.title}</h2>
                <p className="muted">
                  {job.locations.join(' · ')} · {statusLabel(job.employmentType)}
                </p>
              </Link>
            ))}
        </div>
      )}
      {list.hasNextPage && (
        <Button onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
          Показать ещё
        </Button>
      )}
    </>
  );
}
function PublicJob({ id, applying }: { id: string; applying: boolean }) {
  const job = useQuery({
    queryKey: ['public-job', id],
    queryFn: async ({ signal }) => {
      const result = await publicApi.GET('/public/v1/job-postings/{publicId}', {
        params: { path: { publicId: id } },
        signal,
      });
      if (!result.data) throw apiError(result.response, result.error);
      return result.data;
    },
  });
  if (job.isLoading) return <Loading />;
  if (job.error || !job.data) return <ErrorState error={job.error} />;
  return (
    <>
      <Link className="text-link" to="/careers">
        Все вакансии
      </Link>
      <h1>{job.data.title}</h1>
      <p className="muted">
        {job.data.locations.join(' · ')} · {statusLabel(job.data.employmentType)}
      </p>
      {applying ? (
        <PublicApplication id={id} />
      ) : (
        <>
          <section className="panel public-job-description">{job.data.description}</section>
          <Link className="pill primary" to="/careers/$publicId/apply" params={{ publicId: id }}>
            Откликнуться
          </Link>
        </>
      )}
    </>
  );
}
type ApplicationForm = {
  applicationFormVersionId: string;
  processingOptions: { purpose: string; jurisdiction: string; consentRequired: boolean }[];
  privacyNotice: { id: string; text: string };
  sections: { id: string; title: string; order: number; questions: Question[] }[];
};
function PublicApplication({ id }: { id: string }) {
  const form = useQuery({
    queryKey: ['public-form', id],
    queryFn: async ({ signal }) => {
      const result = await publicApi.GET('/public/v1/job-postings/{publicId}/application-form', {
        params: { path: { publicId: id } },
        signal,
      });
      if (!result.data) throw apiError(result.response, result.error);
      return result.data as unknown as ApplicationForm;
    },
  });
  if (form.isLoading) return <Loading />;
  if (form.error || !form.data) return <ErrorState error={form.error} />;
  if (!form.data.processingOptions.length)
    return (
      <Empty
        title="Приём откликов временно недоступен"
        subtitle="Для этой вакансии ещё не настроены условия обработки данных."
      />
    );
  return <ApplicationFields key={form.data.applicationFormVersionId} id={id} form={form.data} />;
}
function ApplicationFields({ id, form }: { id: string; form: ApplicationForm }) {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [option, setOption] = useState(0);
  const [files, setFiles] = useState<Record<string, string[]>>({});
  const [fileNames, setFileNames] = useState<Record<string, string[]>>({});
  const [pending, setPending] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [sent, setSent] = useState(false);
  const rule = form.processingOptions[option]!;
  const set = (key: string, value: unknown) =>
    setValues((previous) => ({ ...previous, [key]: value }));
  const upload = async (question: Question, selected: File[]) => {
    setUploading(question.id);
    setError(null);
    try {
      const session = await publicApi.POST(
        '/public/v1/job-postings/{publicId}/application-upload-sessions',
        {
          params: { path: { publicId: id } },
          headers: headers(),
          body: {
            applicationFormVersionId: form.applicationFormVersionId,
            questionVersionId: question.questionVersionId || question.id,
          },
        },
      );
      if (!session.data) throw apiError(session.response, session.error);
      const grant = session.data;
      if (selected.length > grant.maxFiles)
        throw new Error(`Можно загрузить не больше ${grant.maxFiles} файлов`);
      const ids: string[] = [];
      for (const file of selected) {
        if (file.size > grant.maxFileSizeBytes)
          throw new Error(`Файл «${file.name}» превышает допустимый размер`);
        if (!grant.allowedMimeTypes.includes(file.type))
          throw new Error(`Формат файла «${file.name}» не поддерживается`);
        const body = new FormData();
        body.set('uploadSessionId', grant.uploadSessionId);
        body.set('questionVersionId', grant.questionVersionId);
        body.set('uploadGrant', grant.grantToken);
        body.set('file', file);
        const result = await publicApi.POST('/public/v1/application-files', {
          headers: { ...headers(), 'X-Upload-Grant': grant.grantToken },
          body: body as never,
          bodySerializer: (value) => value as unknown as FormData,
        });
        if (!result.data) throw apiError(result.response, result.error);
        ids.push(result.data.fileId);
      }
      setFiles((previous) => ({ ...previous, [question.id]: ids }));
      setFileNames((previous) => ({
        ...previous,
        [question.id]: selected.map((file) => file.name),
      }));
    } catch (cause) {
      setError(cause as Error);
    } finally {
      setUploading(null);
    }
  };
  if (sent)
    return (
      <section className="panel">
        <h2>Отклик отправлен</h2>
        <p>Спасибо. HR-отдел свяжется с вами по указанной почте.</p>
        <Link to="/careers">Все вакансии</Link>
      </section>
    );
  return (
    <form
      className="domain-form"
      onSubmit={async (event) => {
        event.preventDefault();
        setPending(true);
        setError(null);
        try {
          const questions = form.sections.flatMap((section) => section.questions);
          const answers = applicationAnswers(questions, values, files);
          await publicCommand(
            '/public/v1/job-postings/{publicId}/applications',
            {
              applicationFormVersionId: form.applicationFormVersionId,
              name: values.name,
              primaryEmail: values.primaryEmail,
              country: values.country,
              phone: values.phone || null,
              purpose: rule.purpose,
              jurisdiction: rule.jurisdiction,
              privacyNoticeVersionId: form.privacyNotice.id,
              consentEvidence: rule.consentRequired ? { accepted: true } : null,
              answers,
            },
            { publicId: id },
          );
          setSent(true);
        } catch (cause) {
          setError(cause as Error);
        } finally {
          setPending(false);
        }
      }}
    >
      <section className="panel application-section">
        <h2>Контактные данные</h2>
        <Field
          label="Имя и фамилия"
          name="name"
          required
          value={String(values.name || '')}
          onChange={(event) => set('name', event.target.value)}
        />
        <Field
          label="Электронная почта"
          name="primaryEmail"
          type="email"
          required
          value={String(values.primaryEmail || '')}
          onChange={(event) => set('primaryEmail', event.target.value)}
        />
        <Field
          label="Страна проживания"
          name="country"
          required
          placeholder="Например, RU"
          minLength={2}
          maxLength={2}
          value={String(values.country || '')}
          onChange={(event) => set('country', event.target.value.toUpperCase())}
        />
        <Field
          label="Телефон"
          name="phone"
          type="tel"
          value={String(values.phone || '')}
          onChange={(event) => set('phone', event.target.value)}
        />
      </section>
      {[...form.sections]
        .sort((a, b) => a.order - b.order)
        .map((section) => (
          <section className="panel application-section" key={section.id}>
            <h2>{section.title}</h2>
            {section.questions.map((question) => (
              <Field label={question.label + (question.required ? ' *' : '')} key={question.id}>
                <ApplicationQuestion
                  question={question}
                  value={values[question.id]}
                  onChange={(value) => set(question.id, value)}
                  onUpload={(selected) => void upload(question, selected)}
                  disabled={Boolean(uploading)}
                  fileNames={fileNames[question.id]}
                />
                {uploading === question.id && <span role="status">Загрузка…</span>}
              </Field>
            ))}
          </section>
        ))}
      <section className="panel application-section">
        <h2>Обработка персональных данных</h2>
        {form.processingOptions.length > 1 && (
          <Field label="Юрисдикция">
            <Select
              name="jurisdiction"
              value={option}
              onChange={(event) => setOption(Number(event.target.value))}
            >
              {form.processingOptions.map((rule, index) => (
                <option key={index} value={index}>
                  {rule.jurisdiction} · {rule.purpose}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <p className="privacy-notice">{form.privacyNotice.text}</p>
        <label className="form-check">
          <input name="privacyAccepted" type="checkbox" required />
          {rule.consentRequired
            ? 'Я даю согласие на обработку данных на указанных условиях'
            : 'Я ознакомился с условиями обработки данных'}
        </label>
      </section>
      {error && <ErrorState error={error} />}
      <footer>
        <Link className="pill" to="/careers/$publicId" params={{ publicId: id }}>
          Отмена
        </Link>
        <Button className="primary" type="submit" disabled={pending || Boolean(uploading)}>
          {pending ? 'Отправка…' : 'Отправить отклик'}
        </Button>
      </footer>
    </form>
  );
}
function useFragmentToken() {
  const [token] = useState(
    () => new URLSearchParams(window.location.hash.slice(1)).get('token') || '',
  );
  useEffect(() => {
    if (window.location.hash)
      history.replaceState(history.state, '', window.location.pathname + window.location.search);
  }, []);
  return token;
}
function PublicOffer() {
  const token = useFragmentToken();
  const started = useRef(false);
  const [offer, setOffer] = useState<Record<string, unknown> | null>(null);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [decided, setDecided] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      try {
        if (token) await publicCommand('/public/v1/offer-access-sessions', { token });
        const current = await publicApi.GET('/public/v1/offers/current');
        if (!current.data) throw apiError(current.response, current.error);
        setOffer(current.data);
        if (['SENT', 'VIEWED'].includes(current.data.lifecycleStatus)) {
          const viewed = await publicCommand(
            '/public/v1/offers/current/view',
            { viewedAt: new Date().toISOString() },
            {},
            cookieValue('snaphire_offer_csrf'),
          );
          setOffer(viewed);
        }
      } catch (cause) {
        setError(cause as Error);
      } finally {
        setPending(false);
      }
    })();
  }, [token]);
  const decide = async (decision: string) => {
    setPending(true);
    setError(null);
    try {
      await publicCommand(
        '/public/v1/offers/current/decision',
        { decision, reason: reason || null },
        {},
        cookieValue('snaphire_offer_csrf'),
      );
      setDecided(decision);
    } catch (cause) {
      setError(cause as Error);
    } finally {
      setPending(false);
    }
  };
  if (pending && !offer) return <Loading />;
  const money = offer?.compensation as { amount: string; currency: string } | undefined;
  return (
    <>
      {offer && (
        <>
          <h1>{String(offer.title)}</h1>
          <section className="panel public-job-description">{String(offer.body)}</section>
          <section className="panel details-list">
            <div>
              <span>Вознаграждение</span>
              <span>
                {money
                  ? new Intl.NumberFormat('ru-RU', {
                      style: 'currency',
                      currency: money.currency,
                    }).format(Number(money.amount))
                  : '—'}
              </span>
            </div>
            <div>
              <span>Дата выхода</span>
              <span>{new Date(String(offer.startDate)).toLocaleDateString('ru-RU')}</span>
            </div>
            <div>
              <span>Действует до</span>
              <span>{new Date(String(offer.expiresAt)).toLocaleDateString('ru-RU')}</span>
            </div>
            <div>
              <span>Статус</span>
              <Status value={decided || String(offer.lifecycleStatus)} />
            </div>
          </section>
          {!decided && ['SENT', 'VIEWED'].includes(String(offer.lifecycleStatus)) && (
            <section className="panel application-section">
              <Field label="Комментарий (необязательно)">
                <textarea
                  name="reason"
                  maxLength={2000}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </Field>
              <div className="form-footer">
                <Button disabled={pending} onClick={() => void decide('DECLINED')}>
                  Отклонить
                </Button>
                <Button
                  className="primary"
                  disabled={pending}
                  onClick={() => void decide('ACCEPTED')}
                >
                  Принять оффер
                </Button>
              </div>
            </section>
          )}
        </>
      )}
      {error && <ErrorState error={error} />}
    </>
  );
}
function ConsentWithdrawal() {
  const token = useFragmentToken();
  const started = useRef(false);
  const [ready, setReady] = useState(Boolean(cookieValue('snaphire_privacy_csrf')));
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    setPending(true);
    void publicCommand('/public/v1/consent-withdrawal-sessions', { token })
      .then(() => setReady(true))
      .catch((cause) => setError(cause as Error))
      .finally(() => setPending(false));
  }, [token]);
  return (
    <section className="form-page">
      <h1>Отзыв согласия</h1>
      {result ? (
        <section className="panel">
          <h2>{result === 'LINK_SENT' ? 'Проверьте почту' : 'Запрос принят'}</h2>
          <p>
            {result === 'LINK_SENT'
              ? 'Если для этой почты есть действующее согласие, мы отправим ссылку для его отзыва.'
              : statusLabel(result)}
          </p>
        </section>
      ) : !EMAIL_ENABLED && !ready ? (
        token ? (
          error ? (
            <ErrorState error={error} />
          ) : (
            <Loading />
          )
        ) : (
          <Empty
            title="Запрос через администратора"
            subtitle="Для отзыва согласия обратитесь в организацию, которой вы предоставили данные."
          />
        )
      ) : (
        <form
          className="domain-form"
          onSubmit={async (event) => {
            event.preventDefault();
            const values = new FormData(event.currentTarget);
            setPending(true);
            setError(null);
            try {
              if (ready) {
                const response = await publicCommand(
                  '/public/v1/consents/current/withdraw',
                  { reason: String(values.get('reason') || '') || null },
                  {},
                  cookieValue('snaphire_privacy_csrf'),
                );
                setResult(String(response.status));
              } else {
                await publicCommand('/public/v1/privacy-requests/consent-withdrawal-links', {
                  email: values.get('email'),
                  workspaceSlug: values.get('workspaceSlug'),
                  purpose: values.get('purpose'),
                });
                setResult('LINK_SENT');
              }
            } catch (cause) {
              setError(cause as Error);
            } finally {
              setPending(false);
            }
          }}
        >
          {ready ? (
            <>
              <p className="muted">
                Отзыв ограничит обработку данных, основанную на вашем согласии.
              </p>
              <Field label="Причина (необязательно)">
                <textarea name="reason" maxLength={2000} />
              </Field>
            </>
          ) : (
            <>
              <Field label="Электронная почта" name="email" type="email" required />
              <Field label="Идентификатор организации" name="workspaceSlug" required />
              <Field label="Цель обработки данных" name="purpose" required />
            </>
          )}
          {error && <ErrorState error={error} />}
          <footer>
            <Button className="primary" type="submit" disabled={pending}>
              {pending ? 'Подождите…' : ready ? 'Отозвать согласие' : 'Получить ссылку'}
            </Button>
          </footer>
        </form>
      )}
    </section>
  );
}
